<template>
    <article class="article-content">
        <section class="content-section">
            <h2>{{ sections?.intro?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.intro?.content)"
                :key="'intro-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.what_is_headless?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.what_is_headless?.content)"
                :key="'what-is-headless-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.user_entity_setup?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.user_entity_setup?.content)"
                :key="'user-entity-setup-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/user_entity_definition-400w.webp   400w,
                            /images/blog/headless/user_entity_definition-800w.webp   800w,
                            /images/blog/headless/user_entity_definition-1200w.webp 1200w,
                            /images/blog/headless/user_entity_definition-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/user_entity_definition.png"
                        alt="User entity definition with Password field for headless authentication"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.user_entity_definition }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.registration_flow?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.registration_flow?.content)"
                :key="'registration-flow-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/register_flow-400w.webp   400w,
                            /images/blog/headless/register_flow-800w.webp   800w,
                            /images/blog/headless/register_flow-1200w.webp 1200w,
                            /images/blog/headless/register_flow-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/register_flow.png"
                        alt="User registration flow through RDataCore workflow engine"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.register_flow }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.authentication_flow?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(
                    sections?.authentication_flow?.content
                )"
                :key="'authentication-flow-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/auth_flow-400w.webp   400w,
                            /images/blog/headless/auth_flow-800w.webp   800w,
                            /images/blog/headless/auth_flow-1200w.webp 1200w,
                            /images/blog/headless/auth_flow-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/auth_flow.png"
                        alt="Authentication flow with Entity JWT token generation and protected access"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.auth_flow }}</figcaption>
            </figure>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/from_def-400w.webp   400w,
                            /images/blog/headless/from_def-800w.webp   800w,
                            /images/blog/headless/from_def-1200w.webp 1200w,
                            /images/blog/headless/from_def-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/from_def.png"
                        alt="Workflow FROM step configuration with API source and JSON format"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.from_def }}</figcaption>
            </figure>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/transform_mapping-400w.webp   400w,
                            /images/blog/headless/transform_mapping-800w.webp   800w,
                            /images/blog/headless/transform_mapping-1200w.webp 1200w,
                            /images/blog/headless/transform_mapping-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/transform_mapping.png"
                        alt="Authenticate transform configuration with entity type, identifier and password fields"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.transform_mapping }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.jwt_protection?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.jwt_protection?.content)"
                :key="'jwt-protection-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.headless_use_cases?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.headless_use_cases?.content)"
                :key="'headless-use-cases-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.mdm_as_backend?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.mdm_as_backend?.content)"
                :key="'mdm-as-backend-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.architecture_overview?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(
                    sections?.architecture_overview?.content
                )"
                :key="'architecture-overview-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/headless/headless_architecture-400w.webp   400w,
                            /images/blog/headless/headless_architecture-800w.webp   800w,
                            /images/blog/headless/headless_architecture-1200w.webp 1200w,
                            /images/blog/headless/headless_architecture-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/headless/headless_architecture.png"
                        alt="RDataCore headless architecture with multiple frontends and external integrations"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.headless_architecture }}</figcaption>
            </figure>
        </section>

        <section class="content-section teaser-section">
            <h2>{{ sections?.whats_next?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.whats_next?.content)"
                :key="'whats-next-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="coming-soon-badge">
                <SmartIcon
                    icon="clock"
                    :size="18"
                />
                {{ t('blog.coming_soon') }}: Workflow DSL Deep Dive
            </div>
        </section>
    </article>
</template>

<script setup lang="ts">
    import {
        useBlogFormatter,
        type PostSections,
        type PostCaptions,
    } from '@/composables/useBlogFormatter'
    import { useTranslations } from '@/composables/useTranslations'
    import SmartIcon from '@/components/common/SmartIcon.vue'

    defineProps<{
        sections?: PostSections
        captions?: PostCaptions
    }>()

    const { t } = useTranslations()
    const { splitParagraphs, formatContent } = useBlogFormatter()
</script>
